import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";

/**
 * Veylle custom cursor.
 * - Tiny ink dot follows mouse with spring easing.
 * - Grows into a ring with an eyebrow micro-label on hover over
 *   a, button, [role=button], [data-cursor="hover"] elements.
 * - Hidden on touch devices and when reduced motion is preferred.
 */
export default function Cursor() {
  const reduced = useReducedMotion();
  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches;

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 380, damping: 32, mass: 0.35 });
  const sy = useSpring(y, { stiffness: 380, damping: 32, mass: 0.35 });

  const [hover, setHover] = useState(false);
  const [label, setLabel] = useState("");
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(0);

  useEffect(() => {
    if (reduced || isTouch) return;

    const move = (e) => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        x.set(e.clientX);
        y.set(e.clientY);
        if (!visible) setVisible(true);
      });
    };

    const isInteractive = (el) => {
      if (!el || el.nodeType !== 1) return null;
      return el.closest(
        'a, button, [role="button"], input, textarea, label, [data-cursor="hover"]'
      );
    };

    const over = (e) => {
      const target = isInteractive(e.target);
      if (target) {
        setHover(true);
        setLabel(target.getAttribute("data-cursor-label") || "");
      }
    };
    const out = (e) => {
      const target = isInteractive(e.target);
      if (target) {
        setHover(false);
        setLabel("");
      }
    };
    const leaveDoc = () => setVisible(false);
    const enterDoc = () => setVisible(true);

    window.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("mouseover", over);
    document.addEventListener("mouseout", out);
    document.documentElement.addEventListener("mouseleave", leaveDoc);
    document.documentElement.addEventListener("mouseenter", enterDoc);

    document.documentElement.classList.add("veylle-cursor-active");

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      document.removeEventListener("mouseout", out);
      document.documentElement.removeEventListener("mouseleave", leaveDoc);
      document.documentElement.removeEventListener("mouseenter", enterDoc);
      document.documentElement.classList.remove("veylle-cursor-active");
    };
  }, [reduced, isTouch, visible, x, y]);

  if (reduced || isTouch) return null;

  return (
    <>
      {/* Ring */}
      <motion.div
        data-testid="veylle-cursor"
        aria-hidden
        style={{
          translateX: sx,
          translateY: sy,
        }}
        className="pointer-events-none fixed left-0 top-0 z-[70] mix-blend-difference"
      >
        <motion.div
          animate={{
            width: 10,
            height: 10,
            opacity: visible ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 260, damping: 22, mass: 0.4 }}
          style={{
            transform: "translate(-50%, -50%)",
            border: "1px solid #F3F2EC",
            background: "#F3F2EC",
          }}
          className="rounded-full"
        />
      </motion.div>

      {/* label disabled */}
      {false && label && hover && (
        <motion.div
          aria-hidden
          style={{ translateX: sx, translateY: sy }}
          className="pointer-events-none fixed left-0 top-0 z-[71]"
        >
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ transform: "translate(20px, 20px)" }}
            className="font-mono-stamp text-ivory bg-ink px-2 py-1 whitespace-nowrap"
          >
            {label}
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
